import React from 'react';
import ApiCustomer from '../code/api/ApiCustomer';
import CustomerDataTable from './CustomerDataTable';
import CustomerMessagingForm from './CustomerMessagingForm';
import CustomerInfoForm from './CustomerInfoForm';
import { ButtonBarButton, CustomerListViewFilter, SelectedCustomerView } from '../code/enums/Enums';
import AuthAdminUserModel from '../code/models/AuthAdminUserModel';
import Customer from '../code/models/Customer';
import { Toast } from 'bootstrap';
import BidNavForm from './BidNavForm';
import Api_Config from '../code/api/ApiConfig';
import AddNewCustomer from "./AddNewCustomer";
import ApiAuth from "../code/api/ApiAuth";
import CustomerChangeTracker from "../code/helpers/CustomerChangeTracker";
import { BidNavVersion } from "../code/models/ApiConfigCompany";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";

interface IMainForm { currentAdminUser: AuthAdminUserModel }

export default function MainForm({ currentAdminUser: currentUser }: IMainForm) {
  const toastRef = React.useRef<HTMLDivElement>();
  const [toastMessage, setToastMessage] = React.useState<string>("");
  const [searchData, setSearchData] = React.useState<string>("");
  const [filteredData, setFilteredData] = React.useState<Customer[] | null>(null);
  const [nonFilteredData, setNonFilteredData] = React.useState<Customer[] | null>(null);
  const [selectedCustomer, setSelectedCustomer] = React.useState<Customer | null>(null);
  const [selectedView, setSelectedView] = React.useState<SelectedCustomerView>(SelectedCustomerView.List);
  const [rowCount, setRowCount] = React.useState<number>(0);
  const [viewFilter, setViewFilter] = React.useState<CustomerListViewFilter>(CustomerListViewFilter.ActiveCustomers);
  const company = Api_Config.getSelectedCompany();
  var timerHandle: any = 0;

  React.useEffect(() => {
    // this ensures we have the right static 
    // data when a user hard refreshes a page
    ApiAuth.refreshStaticData().then(() => {
      loadData();
    });
    CustomerChangeTracker.subscribe(onCustomerChanged);
  }, []);

  React.useEffect(() => {
    loadData();
  }, [viewFilter]);

  React.useEffect(() => {
    clearTimeout(timerHandle);
    timerHandle = setTimeout(() => {
      handleDoSearch();
    }, 400);
  }, [nonFilteredData, searchData]);

  React.useEffect(() => {
    if (!!selectedCustomer && !!filteredData) {
      let cust = filteredData.find((c) => c.customerId === selectedCustomer.customerId);

      if (!!cust) {
        setSelectedCustomer(cust);
      } else {
        setSelectedCustomer(null);
      }
    }
  }, [filteredData]);

  function onCustomerChanged(customerId: number) {
    loadData();
  }

  async function loadData() {
    let includeDeactivated = viewFilter === CustomerListViewFilter.AllCustomers || viewFilter === CustomerListViewFilter.InactiveCustomers;

    await ApiCustomer.getCustomers(includeDeactivated).then(data => {
      if (data != null && viewFilter === CustomerListViewFilter.InactiveCustomers) {
        const newData = data.filter(c => c.deactivated);
        setNonFilteredData(newData);
      } else {
        setNonFilteredData(data);
      }
    });
  }

  async function handleDoSearch() {
    if (!nonFilteredData) return;
    if (!searchData) {
      setFilteredData([...nonFilteredData]);
      setRowCount(nonFilteredData.length);
    } else {
      const newFilterData = nonFilteredData.filter(c => {
        if (!c.searchText) {
          let numOnly = (c.phone || "").replace(/\D/g, "");
          c.searchText = (`${c.name}${c.companyId}${c.companyEmployeeId}${c.email}${numOnly}${c.userId}`).toLowerCase();
        }
        return c.searchText.includes(searchData);
      });

      setFilteredData(newFilterData);
      setRowCount(newFilterData.length);
    }
  }

  function getToast(): Toast | null {
    if (toastRef && toastRef.current) {
      var myToast = toastRef.current;
      var bsToast = Toast.getInstance(myToast);

      if (!bsToast) {
        bsToast = new Toast(myToast, { autohide: true });
      }

      return bsToast;
    } else {
      return null;
    }
  }

  function showToast(message: string) {
    setToastMessage(message);
    getToast()?.show();
  }

  function hideToast() {
    getToast()?.hide();
  }

  function getMessageToast() {
    return (
      <div className="toast position-absolute top-50 start-50 translate-middle m-4" role="alert" ref={toastRef as React.RefObject<HTMLDivElement>}>
        <div className="toast-header">
          <strong className="me-auto">Customer Update</strong>
          <small>{selectedCustomer?.name}</small>
          <button type="button" className="btn-close" onClick={() => hideToast()} aria-label="Close"></button>
        </div>
        <div className="toast-body">
          {toastMessage}
        </div>
      </div>
    )
  }

  function handleOnClickEvent(buttonClicked: ButtonBarButton) {
    switch (buttonClicked) {
      case ButtonBarButton.Close:
        if (selectedCustomer || selectedView === SelectedCustomerView.AddNewCustomer) {
          setSelectedView(SelectedCustomerView.List);
        }
        break;
      case ButtonBarButton.CustomerInfo:
        if (selectedCustomer) {
          setSelectedView(SelectedCustomerView.Info);
        }
        break;
      case ButtonBarButton.BidNav:
        if (!selectedCustomer) break;

        if (process.env.REACT_APP_OPEN_BIDNAV_MODE === "embed") {
          setSelectedView(SelectedCustomerView.BidNav);
        } else {
          let url = Api_Config.getBidNavUrlForCustomer(selectedCustomer.customerId, currentUser.id);
          window.open(url);
        }
        break;
      case ButtonBarButton.Messaging:
        if (selectedCustomer) {
          setSelectedView(SelectedCustomerView.Messaging);
        }
        break;
      case ButtonBarButton.AddNewCustomer:
        setSelectedCustomer(null);
        setSelectedView(SelectedCustomerView.AddNewCustomer);
        break;
    }
  }

  function handleCustomerSelected(customer: Customer) {
    setSelectedCustomer(customer);
  }

  function handleCustomerAction(customer: Customer) {
    setSelectedCustomer(customer);
    setSelectedView(SelectedCustomerView.Info);
  }

  function handleCustomerDeleted() {
    setSelectedView(SelectedCustomerView.List);
    loadData();
  }

  function handleCustomerAdded() {
    loadData();
  }

  function handleBidNavClick() {
    handleOnClickEvent(ButtonBarButton.BidNav);
  }

  function handleMessagingClick() {
    handleOnClickEvent(ButtonBarButton.Messaging);
  }

  function handleCustomerInfoClick() {
    handleOnClickEvent(ButtonBarButton.CustomerInfo);
  }

  function handleCloseClick() {
    handleOnClickEvent(ButtonBarButton.Close);
  }

  function handleAddNewClick(): void {
    handleOnClickEvent(ButtonBarButton.AddNewCustomer);
  }

  function getButtonStateClass(isActive: Boolean, additionalClasses: string) {
    const classnames = ["btn"];
    const mainStyle = isActive ? "btn-primary" : "btn-outline-primary";
    classnames.push(mainStyle);

    if (!!additionalClasses) {
      classnames.push(additionalClasses);
    }

    return classnames.join(" ");
  }

  function getCurreFilterDesc(): string {
    switch (viewFilter) {
      case CustomerListViewFilter.ActiveCustomers: return "Active";
      case CustomerListViewFilter.InactiveCustomers: return "Inactive";
      case CustomerListViewFilter.AllCustomers: return "Both";
    }
    return "Active";
  }

  function rotateViewFilter() {
    switch (viewFilter) {
      case CustomerListViewFilter.ActiveCustomers:
        setViewFilter(CustomerListViewFilter.InactiveCustomers);
        break;
      case CustomerListViewFilter.InactiveCustomers:
        setViewFilter(CustomerListViewFilter.AllCustomers);
        break;
      case CustomerListViewFilter.AllCustomers:
      default:
        setViewFilter(CustomerListViewFilter.ActiveCustomers);
        break;
    }
    return "Active";
  }

  function getFilterViewButton() {

    return (
      <React.Fragment>
        <UncontrolledDropdown group key={"viewfltrbtn" + getCurreFilterDesc()}>
          <Button outline color="primary" className="filterbutton" onClick={() => rotateViewFilter()}>{getCurreFilterDesc()}</Button>
          <DropdownToggle caret outline color="primary" />
          <DropdownMenu>
            <DropdownItem disabled={viewFilter === CustomerListViewFilter.ActiveCustomers}
              onClick={() => setViewFilter(CustomerListViewFilter.ActiveCustomers)}>Active</DropdownItem>
            <DropdownItem disabled={viewFilter === CustomerListViewFilter.InactiveCustomers}
              onClick={() => setViewFilter(CustomerListViewFilter.InactiveCustomers)}>Deactivated</DropdownItem>
            <DropdownItem disabled={viewFilter === CustomerListViewFilter.AllCustomers}
              onClick={() => setViewFilter(CustomerListViewFilter.AllCustomers)}>Both</DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </React.Fragment>
    )
  }

  function createCloseButton(): React.ReactNode {
    return (
      <React.Fragment>
        {selectedView !== SelectedCustomerView.List && <button onClick={handleCloseClick} className="btn btn-info">Close</button>}
      </React.Fragment>
    )
  }

  function createAddCustomerButton(): React.ReactNode {
    return (
      <React.Fragment>
        {company.bidNavVersion === BidNavVersion.BidNav20 && selectedView === SelectedCustomerView.List && currentUser?.isAdmin &&
          <button onClick={handleAddNewClick} className="btn btn-outline-primary addnew">Add Customer</button>}
      </React.Fragment>
    )
  }

  function addQuickSearchInput() {
    return (
      <React.Fragment>
        <div className="col-2 me-2 quicksearch">
          <input type="search" className="form-control" placeholder='Quick Search' defaultValue={searchData}
            onChange={(e) => {
              setSearchData(e.target.value.toLowerCase());
            }}></input>
        </div>
      </React.Fragment>
    )
  }

  return (
    <main className='main-content'>
      <div className='buttonbar'>
        {selectedView === SelectedCustomerView.List && getFilterViewButton()}
        {selectedView === SelectedCustomerView.List && addQuickSearchInput()}
        <span className="text-primary name filler" key={`selcustedt${selectedCustomer?.customerId || 0}`} >{selectedCustomer?.name}</span>
        {createCloseButton()}
        {createAddCustomerButton()}
        <button
          onClick={handleCustomerInfoClick}
          className={getButtonStateClass(selectedView === SelectedCustomerView.Info, "")}
          disabled={!selectedCustomer}>Customer Info</button>
        <button
          onClick={handleMessagingClick}
          className={getButtonStateClass(selectedView === SelectedCustomerView.Messaging, "")}
          disabled={!selectedCustomer}>Messaging</button>
        <button onClick={handleBidNavClick} className="btn btn-outline-primary" disabled={!selectedCustomer}>BidNav</button>
      </div>
      {selectedView === SelectedCustomerView.Info && selectedCustomer && <CustomerInfoForm customer={selectedCustomer} currentUser={currentUser} showToast={showToast} customerDeleted={handleCustomerDeleted} />}
      {selectedView === SelectedCustomerView.Messaging && selectedCustomer && <CustomerMessagingForm currentUser={currentUser} customer={selectedCustomer} />}
      {selectedView === SelectedCustomerView.List &&
        <CustomerDataTable tableData={filteredData} currentCustomerId={!!selectedCustomer ? selectedCustomer.customerId : 0}
          customerSelectedCallback={handleCustomerSelected} customerActionCallback={handleCustomerAction} />}
      {selectedView === SelectedCustomerView.BidNav && selectedCustomer && <BidNavForm customer={selectedCustomer} adminUser={currentUser} />}
      {selectedView === SelectedCustomerView.AddNewCustomer && <AddNewCustomer customerAdded={handleCustomerAdded} />}
      {getMessageToast()}
      {selectedView === SelectedCustomerView.List && rowCount > 0 &&
        <span className="customer-row-count">Customer Count: {rowCount}</span>}
    </main>
  )
}
